'use client';

import { CheckCircle2, ChevronDown } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { USER_ROLE_OPTIONS, ENTITY_ROLE_OPTIONS, ROLE_CATEGORIES } from '@sizzlenz/shared';
import { MultiSelectDropdown } from '@/components/ui/multi-select-dropdown';
import { useState } from 'react';

interface RoleSelectorProps {
  selectedRoles: string[];
  additionalDetails: string;
  onRolesChange: (roles: string[]) => void;
  onAdditionalDetailsChange: (names: string) => void;
  enableAdditionalDetails?: boolean;
  requireAdditionalDetails?: boolean;
  disabled?: boolean;
  variant?: 'user' | 'entity';
  displayAs?: 'list' | 'select';
}

export function RoleSelector({
  selectedRoles,
  additionalDetails,
  onRolesChange,
  enableAdditionalDetails = true,
  requireAdditionalDetails = false,
  onAdditionalDetailsChange,
  disabled = false,
  variant = 'user',
  displayAs = 'list',
}: RoleSelectorProps) {
  const roleOptions = variant === 'entity' ? ENTITY_ROLE_OPTIONS : USER_ROLE_OPTIONS;

  // Track which categories are expanded (only those with selections are expanded by default)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    const grouped = ROLE_CATEGORIES.map((category) => ({
      id: category.id,
      roles: (variant === 'entity' ? ENTITY_ROLE_OPTIONS : USER_ROLE_OPTIONS).filter(
        (role) => 'category' in role && role.category === category.id
      ),
    }));
    const withSelections = grouped
      .filter((g) => g.roles.some((r) => selectedRoles.includes(r.id)))
      .map((g) => g.id);
    return new Set(withSelections);
  });

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const toggleRole = (roleId: string) => {
    if (selectedRoles.includes(roleId)) {
      onRolesChange(selectedRoles.filter((role) => role !== roleId));
    } else {
      onRolesChange([...selectedRoles, roleId]);
    }
  };

  const showAdditionalDetailsField = enableAdditionalDetails;
  // DON'T UNCOMMENT
  // const showAdditionalDetailsField = selectedRoles.some((role) =>
  //   [
  //     'brand',
  //     'manufacturer',
  //     'contract-manufacturer',
  //     'contract-packer',
  //     'distributor',
  //     'food-retailer',
  //     'foodservice-business',
  //     'non-food-retailer',
  //     'supermarket',
  //     'workplace',
  //     'service-provider',
  //   ].includes(role)
  // );

  // Convert role options to multi-select format
  const multiSelectOptions = roleOptions.map((role) => ({
    id: role.id,
    label: role.label,
  }));

  // Group roles by category
  const groupedRoles = ROLE_CATEGORIES.map((category) => ({
    ...category,
    roles: roleOptions.filter((role) => 'category' in role && role.category === category.id),
  })).filter((group) => group.roles.length > 0);

  return (
    <div className="space-y-3">
      {displayAs === 'select' ? (
        <MultiSelectDropdown
          label={variant === 'entity' ? 'Entity Roles' : 'User Roles'}
          placeholder="Select roles..."
          options={multiSelectOptions}
          selectedIds={selectedRoles}
          onSelectionChange={onRolesChange}
          showChips={true}
          showSummaryInTrigger={false}
          showBulkActions={true}
          disabled={disabled}
          showSearch={true}
          searchPlaceholder="Search roles..."
        />
      ) : (
        <div className="space-y-2">
          {groupedRoles.map((group) => {
            const isExpanded = expandedCategories.has(group.id);
            const selectedInGroup = group.roles.filter((r) => selectedRoles.includes(r.id)).length;

            return (
              <div key={group.id} className="rounded-xl border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleCategory(group.id)}
                  disabled={disabled}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors cursor-pointer',
                    selectedInGroup > 0 ? 'bg-orange-50' : 'bg-slate-50 hover:bg-slate-100',
                    disabled && 'cursor-not-allowed opacity-50'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-700">{group.label}</span>
                    {selectedInGroup > 0 && (
                      <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-orange-500 text-[11px] font-medium text-white">
                        {selectedInGroup}
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-slate-400 transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>

                {isExpanded && (
                  <div className="px-1 py-1 border-t border-slate-100">
                    {group.roles.map((role) => {
                      const selected = selectedRoles.includes(role.id);
                      return (
                        <button
                          key={role.id}
                          type="button"
                          onClick={() => toggleRole(role.id)}
                          disabled={disabled}
                          className={cn(
                            'w-full rounded-lg px-3 py-2 text-left transition-all cursor-pointer',
                            selected ? 'bg-orange-50' : 'bg-white hover:bg-orange-50/40',
                            disabled && 'cursor-not-allowed opacity-50'
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={cn(
                                'flex h-4 w-4 items-center justify-center rounded border-2 transition-colors shrink-0',
                                selected
                                  ? 'border-orange-500 bg-orange-500'
                                  : 'border-slate-300 bg-white'
                              )}
                            >
                              {selected && <CheckCircle2 className="h-3 w-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span
                                className={cn(
                                  'text-sm font-medium',
                                  selected ? 'text-orange-700' : 'text-slate-900'
                                )}
                              >
                                {role.label}
                              </span>
                              {role.description && (
                                <span className="ml-1.5 text-xs text-slate-400">
                                  {role.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAdditionalDetailsField && (
        <div className="space-y-2">
          <Label htmlFor="additionalDetails">
            Tell us more{requireAdditionalDetails && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <p className="text-sm text-slate-500">
            Please enter the names of your brands, businesses, workplaces, orgnisations, and any
            other details you think we should know. This helps us approve and set up your account
            and get you Sizzling asap!
          </p>
          <textarea
            id="additionalDetails"
            value={additionalDetails}
            onChange={(e) => onAdditionalDetailsChange(e.target.value)}
            placeholder="e.g. Brand X, Retailer Y"
            rows={3}
            required={requireAdditionalDetails}
            className={cn(
              'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-100',
              requireAdditionalDetails && !additionalDetails.trim() && 'border-red-300'
            )}
            disabled={disabled}
          />
          {requireAdditionalDetails && !additionalDetails.trim() && (
            <p className="text-xs text-red-500">This field is required</p>
          )}
        </div>
      )}
    </div>
  );
}
